import { calculateCube } from '@/utils/measures';
import { WeightUomSelect } from '@views/screens/common';
import { Col, Row } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import FilterCard from '../../../../../../components/Cards/FilterCard';
import FormCheckBox from '../../../../../../components/Inputs/FormCheckBox';
import FormInput from '../../../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import ProductDimentionsUomSelector from '../../../../../../components/Selectors/ProductDimentionsUomSelector';
import ProductUnitUomSelector from '../../../../../../components/Selectors/ProductUnitUomSelector';
import ShipmentProductClassSelector from '../../../../../../components/Selectors/ShipmentProductClassSelector';
import SaveCancelButtons from '../../../SaveCancelButtons';

const TMSProductDetailsCard = ({
  index,
  name,
  remove,
  itemDetails = {},
  handleDataChange,
  handleSubmit,
  defaultValues = {},
  isLast = false,
}) => {
  let { editMode, isNew } = itemDetails;
  editMode = Boolean(editMode);

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const setEditMode = (val) => {
    handleDataChange({ editMode: val }, index);
  };

  const handleSave = () => {
    const data = { ...itemDetails, editMode: false, isNew: false };
    handleSubmit(data, index);
  };

  const handleCancel = () => {
    if (isNew) {
      remove(name);
    } else {
      handleDataChange({ ...defaultValues, editMode: false }, index);
    }
  };

  const cube = useMemo(
    () =>
      calculateCube(
        itemDetails?.length,
        itemDetails?.width,
        itemDetails?.height,
        itemDetails?.billQty,
        itemDetails?.dimUom,
      ),
    [itemDetails?.length, itemDetails?.width, itemDetails?.height, itemDetails?.billQty, itemDetails?.dimUom],
  );

  useEffect(() => {
    if (cube) {
      handleDataChange({ cube }, index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cube]);

  useEffect(() => {
    handleDataChange({ itemSequenceKey: index + 1 }, index);
  }, [index]);

  return (
    <FilterCard
      shadow={false}
      showBorder
      className={isLast ? '' : 'mb-15'}
      customHeader={
        <div className="w-100">
          <Row justify="space-between" align="middle">
            <Col>
              <FormCheckBox
                name={[name, 'selected']}
                checkBoxClass="normal-checkbox"
                className="m-0"
                label={
                  <>
                    <FormLabel className="fw-400">{`${t('translation:getQuote.productDetails.itemNo')} :`}</FormLabel>{' '}
                    <FormLabel>{String(index + 1).padStart(2, '00')}</FormLabel>
                  </>
                }
              />
            </Col>
            <Col>
              <SaveCancelButtons
                onSave={handleSave}
                editMode={editMode}
                handleRemove={() => remove(name)}
                setEditMode={setEditMode}
                onCancel={handleCancel}
              />
            </Col>
          </Row>
        </div>
      }
    >
      <div className="p-15">
        <Row gutter={20}>
          <Col sm={4}>
            <ProductUnitUomSelector
              label={t('translation:default.fields.packageType.label')}
              name={[name, 'billQtyUom']}
              editMode={editMode}
              margin="m-0"
              rules={[
                {
                  required: true,
                  message: t('translation:default.fields.packageType.message'),
                },
              ]}
              onChange={(val) => handleDataChange({ billQtyUomName: val?.label }, index)}
              maxLength={4}
            />
          </Col>
          <Col sm={4}>
            <FormInput
              name={[name, 'billQty']}
              label={t('translation:getQuote.productDetails.numberOfPackages.label')}
              margin="m-0"
              editMode={editMode}
              inputType="number"
              rules={[
                {
                  required: true,
                  message: t('translation:getQuote.productDetails.numberOfPackages.message'),
                },
              ]}
            />
          </Col>
          <Col sm={4}>
            <Row gutter={15}>
              <Col sm={12}>
                <FormInput
                  name={[name, 'weight']}
                  label={t('translation:getQuote.productDetails.weight.label')}
                  margin="m-0"
                  editMode={editMode}
                  inputType="number"
                  placeholder={t('translation:default.fields.weight')}
                  rules={[
                    {
                      required: true,
                      message: t('translation:getQuote.productDetails.weight.message'),
                    },
                  ]}
                />
              </Col>
              <Col sm={12}>
                <WeightUomSelect
                  name={[name, 'weightUom']}
                  label={t('translation:getQuote.productDetails.unit.label')}
                  margin="m-0"
                  editMode={editMode}
                  placeholder={t('translation:default.buttons.select')}
                  onChange={(val) => handleDataChange({ weightUomName: val?.label }, index)}
                />
              </Col>
            </Row>
          </Col>
          <Col sm={6}>
            <Row gutter={10}>
              <Col sm={6}>
                <FormInput
                  name={[name, 'length']}
                  label={t('translation:default.fields.length')}
                  margin="m-0"
                  editMode={editMode}
                  inputType="number"
                  placeholder="00"
                />
              </Col>
              <Col sm={6}>
                <FormInput
                  name={[name, 'width']}
                  label={t('translation:default.fields.width')}
                  margin="m-0"
                  editMode={editMode}
                  inputType="number"
                  placeholder="00"
                />
              </Col>
              <Col sm={6}>
                <FormInput
                  name={[name, 'height']}
                  label={t('translation:default.fields.height')}
                  margin="m-0"
                  editMode={editMode}
                  inputType="number"
                  placeholder="00"
                />
              </Col>
              <Col sm={6}>
                <ProductDimentionsUomSelector
                  name={[name, 'dimUom']}
                  label={t('translation:getQuote.productDetails.unit.label')}
                  margin="m-0"
                  editMode={editMode}
                  placeholder={t('translation:getQuote.productDetails.unit.label')}
                  onChange={(val) => handleDataChange({ dimUomName: val?.label }, index)}
                />
              </Col>
            </Row>
          </Col>
          <Col sm={3}>
            <FormInput
              name={[name, 'cube']}
              label={t('translation:default.fields.cube.label')}
              margin="m-0"
              editMode={editMode}
              inputType="number"
              placeholder="00"
            />
          </Col>
          <Col sm={3}>
            <ShipmentProductClassSelector
              name={[name, 'class']}
              label="Estimated Freight Class"
              placeholder="Select a estimated freight class"
              margin="m-0"
              editMode={editMode}
              onChange={(val) => handleDataChange({ className: val?.label }, index)}
            />
          </Col>
        </Row>
      </div>
      <HiddenFormInput name={[name, 'editMode']} />
      <HiddenFormInput name={[name, 'isNew']} />
      <HiddenFormInput name={[name, 'itemSequenceKey']} />
      <HiddenFormInput name={[name, 'gridOperation']} />
      <HiddenFormInput name={[name, 'createdBy']} />
      <HiddenFormInput name={[name, 'billQtyUomName']} />
      <HiddenFormInput name={[name, 'className']} />
      <HiddenFormInput name={[name, 'dimUomName']} />
      <HiddenFormInput name={[name, 'weightUomName']} />
    </FilterCard>
  );
};
export default TMSProductDetailsCard;
